import { IconNames } from "@src/straps/base/icons/Icon/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import React, {
  FunctionComponent,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchPath, useLocation } from "react-router-dom";
import { LeftNavItemRow, ROW_WITH_SEPARATOR_HEIGHT } from "./LeftNavItemRow";
import {
  useCollapseSiblingsOnPathChange,
  useIsLeftNavCollapsed,
  useIsPathExpanded,
  useSetPathExpanded,
} from "./state";
import { VerticalExpandPanel } from "./VerticalExpandPanel";

// Update scroll position until height transitions within the scroll panel settle
const scrollToOffset = (
  offset: number,
  scrollRef: RefObject<HTMLDivElement>
) => {
  let lastHeight = 0;
  const handleHeightTransition = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = offset;
      if (Math.abs(scrollRef.current.scrollHeight - lastHeight) > 1) {
        lastHeight = scrollRef.current.scrollHeight;
        requestAnimationFrame(handleHeightTransition);
      }
    }
  };
  handleHeightTransition();
};

const VirtualizedNavItemList = ({
  count,
  scrollToIndex,
  renderItem,
  initialEstimateSize,
}: {
  readonly count: number;
  readonly scrollToIndex: number;
  readonly renderItem: (index: number) => React.ReactNode;
  readonly initialEstimateSize: number;
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => initialEstimateSize,
  });

  useEffect(() => {
    if (scrollToIndex >= 0) {
      const range = virtualizer.calculateRange();
      const isInRange =
        range &&
        scrollToIndex >= range.startIndex &&
        scrollToIndex <= range.endIndex;
      if (!isInRange) {
        // Only scroll if the scrollToIndex is outside of the range currently rendering
        scrollToOffset(
          virtualizer.measurementsCache[scrollToIndex]!.start,
          parentRef
        );
      }
    }
  }, [scrollToIndex, virtualizer]);

  return (
    <div
      className="max-h-[630px] overflow-y-auto overflow-x-hidden"
      ref={parentRef}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.index}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            {renderItem(virtualRow.index)}
          </div>
        ))}
      </div>
    </div>
  );
};

const LeftNavItemContainer: FunctionComponent<
  React.PropsWithChildren<{ readonly level: number }>
> = ({ level, children }) => (
  <div
    className={classNames({
      "border-b border-b-straps-accent-3": level === 0,
    })}
  >
    {children}
  </div>
);

interface LeftNavItemProps {
  title: string;
  titleElement?: ReactElement;
  iconName?: IconNames;
  disabled?: boolean;
  loading?: boolean;
  // This is called in addition to expanding (if node has children), or navigating (if node has path), when clicked
  onClick?: () => void;
  // Only displays as active if url matches exact
  exact?: boolean;
  // This acts a key, so it must be unique, and parent paths should be substrings of child paths
  path?: string;
  // Array of query string keys to maintain when navigating to path
  persistQueries?: string[];

  /* Begin props for parent nodes only */

  isDefaultExpanded?: boolean;
  children?:
    | ReactElement<LeftNavItemProps>
    | (ReactElement<LeftNavItemProps> | false)[];
  // When true, collapse all siblings when expanded
  collapseSiblingsOnExpand?: boolean;
  // Caps the height of the children, virtualizes the list, and auto-scrolls to active children
  virtualizedChildren?: boolean;

  /* End props for parent nodes only */

  // This prop is maintained automatically and should not be set by consumers
  level?: number; // Level in the tree
  onSetActive?: () => void; // To make pathless parents aware of active children
}

const ParentLeftNavItem = ({
  path,
  level = 0,
  isDefaultExpanded = false,
  collapseSiblingsOnExpand = false,
  virtualizedChildren,
  children,
  isExpanded,
  setExpanded,
  onSetActive,
  ...rowProps
}: Readonly<
  LeftNavItemProps & {
    isExpanded: boolean;
    setExpanded: (expanded: boolean) => void;
  }
>) => {
  const isLeftNavCollapsed = useIsLeftNavCollapsed();
  const childrenArray = useMemo(
    () => React.Children.toArray(children) as ReactElement<LeftNavItemProps>[],
    [children]
  );
  const location = useLocation();
  const activeChildIndex = useMemo(
    () =>
      childrenArray.findIndex(({ props: { path: childPath } }) =>
        matchPath(location.pathname, {
          path: childPath,
        })
      ),
    [childrenArray, location]
  );

  useEffect(() => {
    activeChildIndex >= 0 && onSetActive?.();
  }, [activeChildIndex, onSetActive]);

  const showChildren = children && isExpanded && !isLeftNavCollapsed;

  useEffect(() => {
    if (isDefaultExpanded) {
      // Allow location update to happen first
      requestAnimationFrame(() => {
        setExpanded(true);
      });
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderChild = useCallback(
    (index: number) => {
      const child = childrenArray[index];
      if (!React.isValidElement(child)) {
        return null;
      }
      return React.cloneElement(child, {
        level: level + 1,
      });
    },
    [childrenArray, level]
  );

  return (
    <LeftNavItemContainer level={level}>
      <LeftNavItemRow
        path={path}
        hasChildren={Boolean(children)}
        setExpanded={setExpanded}
        isExpanded={isExpanded}
        level={level}
        isLeftNavCollapsed={isLeftNavCollapsed}
        isChainActiveOverride={activeChildIndex >= 0}
        {...rowProps}
      />

      {childrenArray.length > 0 && (
        <VerticalExpandPanel isOpen={showChildren}>
          {virtualizedChildren ? (
            <VirtualizedNavItemList
              count={childrenArray.length}
              scrollToIndex={activeChildIndex}
              renderItem={renderChild}
              initialEstimateSize={ROW_WITH_SEPARATOR_HEIGHT}
            />
          ) : (
            <>{childrenArray.map((_, idx) => renderChild(idx))}</>
          )}
        </VerticalExpandPanel>
      )}
    </LeftNavItemContainer>
  );
};

const ParentPathLeftNavItem = ({
  path,
  collapseSiblingsOnExpand = false,
  ...props
}: Readonly<LeftNavItemProps & { path: string }>) => {
  const setExpanded = useSetPathExpanded(path, { collapseSiblingsOnExpand });
  useCollapseSiblingsOnPathChange(path, collapseSiblingsOnExpand);
  const isExpanded = useIsPathExpanded(path);

  return (
    <ParentLeftNavItem
      setExpanded={setExpanded}
      isExpanded={isExpanded}
      path={path}
      {...props}
    />
  );
};

// Less used version that does not use the path state system to expand/collapse.
// This means it cannot collapse siblings, use virtualized children, or show active chains deeper than 1 level
const PathlessLeftNavItem = (props: Readonly<LeftNavItemProps>) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const onChildSetActive = useCallback(() => {
    setIsExpanded(true);
  }, [setIsExpanded]);

  return (
    <ParentLeftNavItem
      setExpanded={setIsExpanded}
      isExpanded={isExpanded}
      onSetActive={onChildSetActive}
      {...props}
    />
  );
};

const LeafLeftNavItem = ({
  level = 0,
  path,
  ...rowProps
}: Readonly<LeftNavItemProps & { path: string }>) => {
  const isLeftNavCollapsed = useIsLeftNavCollapsed();

  return (
    <LeftNavItemContainer level={level}>
      <LeftNavItemRow
        level={level}
        isLeftNavCollapsed={isLeftNavCollapsed}
        path={path}
        {...rowProps}
      />
    </LeftNavItemContainer>
  );
};

export const LeftNavItem = (props: Readonly<LeftNavItemProps>) => {
  if (props.path) {
    if (props.children) {
      return <ParentPathLeftNavItem {...props} path={props.path} />;
    }
    return <LeafLeftNavItem {...props} path={props.path} />;
  }

  return <PathlessLeftNavItem {...props} />;
};
